<template lang="pug">
.celebration-manage
    .header
        h3.title {{$t(`${BASE_TRANSLATION_PATH}.title`)}}
        .add-section
          aiq-select.lang-select(v-if="availableLanguages.length > 1" v-model="selectedLanguage", placeholder="Select Language", size="small" :suffix-icon="Icon.CaretBottom")
            aiq-option(v-for="item in availableLanguages", :key="item.code", :label="item.name", :value="item.code")
          aiq-tooltip(effect="dark",
             :content="$t(`${BASE_TRANSLATION_PATH}.add_celebration`)",
             placement="bottom",
             :show-after="500")
            i.el-icon-plus(@click="openEditor(null, null)" class="add-icon add-celebration-btn")
    aiq-table(:data="celebrationList"
          class="celebration-setting-table")
        aiq-table-column(:label="$t(`${BASE_TRANSLATION_PATH}.celebration_title`)"
              label-class-name="celebration-tb-header"
              min-width="10")
          template(v-slot:default="scope")
            span {{ scope.row.type }}

        aiq-table-column(label-class-name="celebration-tb-header"
              min-width="10")
          template(v-slot:header)
            span {{ $t(`${BASE_TRANSLATION_PATH}.occurrence`) }}&nbsp;
            aiq-tooltip(effect="dark"
                  :show-after="1000"
                  :content="$t(`${BASE_TRANSLATION_PATH}.tooltip.occurrence`)")
              fa-icon.question-mark(:icon="['fas', 'circle-question']")
          template(v-slot:default="scope")
            aiq-select(
                  v-model="scope.row.occurrence"
                  size="small"
                  :clearable="false"
                  :suffix-icon="Icon.CaretBottom"
                  :clear-icon="Icon.Close"
                  class="sel-occurrence"
                  @change="onSaveCelebration(scope.row, scope.$index)")
              aiq-option(v-for="item in CELEBRATION_OCCURRENCE_TYPES",
                    :key="item.value",
                    :label="item.label",
                    :value="item.value")

        aiq-table-column(label-class-name="celebration-tb-header"
              min-width="5")
          template(v-slot:header)
            span {{ $t(`${BASE_TRANSLATION_PATH}.rank`) }}&nbsp;
            aiq-tooltip(effect="dark"
                  :show-after="1000"
                  :content="$t(`${BASE_TRANSLATION_PATH}.tooltip.rank`)")
              fa-icon.question-mark(:icon="['fas', 'circle-question']")
          template(v-slot:default="scope")
            aiq-select(
                  :ref="'rankSelect_' + scope.$index"
                  v-model="scope.row.rank"
                  size="small"
                  :clearable="false"
                  :suffix-icon="Icon.CaretBottom"
                  :clear-icon="Icon.Close"
                  class="sel-rank"
                  @change="onRankChange(scope.row, scope.$index)")
              aiq-option(v-for="item in getRank",
                    :key="item",
                    :label="item",
                    :value="item")

        aiq-table-column(:label="$t(`${BASE_TRANSLATION_PATH}.visual`)"
              label-class-name="celebration-tb-header"
              min-width="10")
          template(v-slot:default="scope")
            aiq-select(
                  v-model="scope.row.visual"
                  size="small"
                  :filterable="true"
                  :clearable="false"
                  :suffix-icon="Icon.CaretBottom"
                  :clear-icon="Icon.Close"
                  class="sel-visual"
                  @change="onSaveCelebration(scope.row, scope.$index)")
              aiq-option(v-for="item in CELEBRATION_VISUAL_TYPES",
                    :key="item.value",
                    :label="item.label",
                    :value="item.value")
        aiq-table-column(min-width="2" label-class-name="celebration-tb-header")
          template(v-slot:default="scope")
            aiq-button(link type="primary" size="default" @click="openEditor(scope.row, scope.$index)" class="edit-btn") {{ $t(`${BASE_TRANSLATION_PATH}.edit`) }}

    celebration-edit(:isVisible="isModalVisible"
          :celebration="selectedCelebration"
          :celebrationIndex="selectedIndex"
          :availableLanguages="availableLanguages"
          :celebrationLang="selectedLanguage"
          :celebrationVisuals="CELEBRATION_VISUAL_TYPES"
          @save="onSaveCelebration"
          @delete="deleteCelebration"
          @close="closeEditor")
    CustomConfetti(@update="confettiList" :confettiList="confettiListItems")
</template>

<script>
import { AVAILABLE_LANGUAGES, DEFAULT_SYSTEM_LANGUAGE, CELEBRATION_OCCURRENCE_TYPES, CELEBRATION_VISUAL_TYPES, CELEBRATION_RANK_LIMIT } from '@/constants/settings';
import CelebrationEdit from './Edit.vue';
import CustomConfetti from './CustomConfetti.vue';
import get from 'lodash/get';

export default {
  name: 'celebration-manage',
  components: {
    CelebrationEdit,
    CustomConfetti,
  },
  data() {
    return {
      BASE_TRANSLATION_PATH: 'settings_tab.admin_tab.celebration',
      celebrationList: [],
      confettiListItems: [],
      list: {},
      availableLanguages: [],
      selectedLanguage: null,
      celebration_key: 'celebration',
      selectedCelebration: null,
      selectedIndex: null,
      isModalVisible: false,
      CELEBRATION_OCCURRENCE_TYPES,
      CELEBRATION_VISUAL_TYPES,
      CELEBRATION_RANK_LIMIT,
    };
  },
  watch: {
    selectedLanguage(newValue) {
      if (!newValue || newValue === 'en') {
        this.celebration_key = 'celebration';
      } else {
        this.celebration_key = `celebration_${newValue}`;
      }
      this.celebrationList = this.list[this.celebration_key];
    },
  },
  computed: {
    getRank() {
      return Array.from({ length: this.CELEBRATION_RANK_LIMIT }, (_, i) => i + 1);
    },
  },
  async mounted() {
    const webchatLangSettings = await this.$store.dispatch('configs/getWebchatLanguageSettings');
    const enabledLangs = webchatLangSettings.enabled_languages || [];
    this.availableLanguages = AVAILABLE_LANGUAGES.filter(lang => enabledLangs.includes(lang.code));

    if (this.availableLanguages.length === 1) {
      this.selectedLanguage = this.availableLanguages[0].code;
    }
    else if (this.availableLanguages.length > 1) {
      if (this.availableLanguages.find(lang => lang.code === DEFAULT_SYSTEM_LANGUAGE.code)) {
        this.selectedLanguage = DEFAULT_SYSTEM_LANGUAGE.code;
      }
      else {
        this.selectedLanguage = this.availableLanguages[0].code;
      }
    }

    this.list = await this.$store.dispatch('settings/getSetting', {
      name: 'celebration',
    });
    this.celebrationList = this.list[this.celebration_key];
    const confettiList = await this.$store.dispatch('settings/getSetting', {
      name: 'custom_confetti',
    });
    this.confettiListItems = get(confettiList, 'list', []);
    this.confettiList(this.confettiListItems);
  },

  methods: {
    confettiList(confettiList) {
      const list = confettiList.map(item => ({
        label: item.name,
        value: item.name,
      }));
      //filter the list in alphabetical order
      const confettiListSort = [...CELEBRATION_VISUAL_TYPES, ...list].sort((a, b) => a.label.localeCompare(b.label));
      //if confettti does not exist eg deleted from custom confetti, set to none
      this.celebrationList = this.celebrationList.map(item => {
        if (!confettiListSort.find(confetti => confetti.value === item.visual)) {
          return { ...item, visual: 'none' };
        }
        return item;
      });
      this.CELEBRATION_VISUAL_TYPES = confettiListSort;
    },
    async saveCelebration(actionType) {
      try {
        this.list[this.celebration_key] = this.celebrationList;
        await this.$store.dispatch('settings/updateSetting', {
          payload: this.list,
          name: 'celebration',
        });
        if (actionType) {
          this.$aiq.notify.success(this.$t(`${this.BASE_TRANSLATION_PATH}.${actionType}`));
        }
      } catch (err) {
        this.$aiq.notify.error(err.message);
      }
    },
    openEditor(record, index) {
      this.selectedCelebration = record ? { ...record } : null;
      this.selectedIndex = index;
      this.isModalVisible = true;
    },
    closeEditor() {
      this.isModalVisible = false;
    },
    async deleteCelebration(index) {
      this.celebrationList.splice(index, 1);
      await this.saveCelebration('deleted');
      this.closeEditor();
    },
    onRankChange(row, index) {
      this.onSaveCelebration(row, index);

      // Remove focus
      const selectComponent = this.$refs[`rankSelect_${index}`];
      if (selectComponent) {
        selectComponent.blur?.();
      }
    },
    async onSaveCelebration(record, index, lang = null) {
      let actionType = 'saved';
      if (typeof index === 'number' && index >= 0) {

        if (!lang) {
          lang = this.selectedLanguage;
        }

        // Edit mode
        if (this.selectedLanguage === lang) {
          if (this.celebrationList[index]) {
            this.celebrationList[index] = record;
          }
          else {
            return;
          }
        }
        else {
          // Delete first and then add new one in different language
          this.celebrationList.splice(index, 1);

          if (lang === 'en') {
            this.celebration_key = 'celebration';
          } else {
            this.celebration_key = `celebration_${lang}`;
          }
          this.celebrationList = this.list[this.celebration_key];

          this.celebrationList.push(record);
          actionType = 'saved';

        }
      } else {
        // Add mode
        this.celebrationList.push(record);
        actionType = 'added';
      }

      this.celebrationList.sort((a, b) => (a.rank || 0) - (b.rank || 0));

      await this.saveCelebration(actionType);
      this.closeEditor();

      // Update language if it's different from the selected language
      if (this.selectedLanguage !== lang) {
        this.selectedLanguage = lang;
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.celebration-manage {
  padding-top: 5px;

  .header {
    .title {
      font-size: 16px;
      font-weight: 600;
    }
  }

  .add-celebration-btn {
    font-size: 14px;
    line-height: 14px;
    vertical-align: middle;
  }

  .lang-select {
    width: 200px;
    padding-right: 20px;
  }


  .celebration-setting-table {
    border: 1px solid #A8A9AB;
    border-radius: 2px;

    ::v-deep .celebration-tb-header {
      border-bottom: 1px solid #A8A9AB;
      .cell {
        padding: 8px;
        line-height: 18px;
        font-weight: 400;
        color: #28292C;
        background: #DDD;
      }
    }

    ::v-deep .el-table__body-wrapper {
      table.el-table__body {
        tbody {
          tr {
            td {
              padding: 5px 0px;

              .sel-occurrence {
                max-width: 200px;
              }
              .sel-rank {
                max-width: 100px;
              }
              .sel-visual {
                max-width: 200px;
              }
              .edit-btn {
                float: right;
              }
            }
          }
        }
      }
    }
  }

}

.el-collapse-item__wrap {
  background-color: #f6f6f6;
}

.add-icon {
    cursor: pointer;
}

.add-section {
  float: right;
}
</style>